import { StrictOmit } from "@instacover/utils"
import { UpdateSpec } from "dexie"

import { deletedFactory } from "@/database/helpers/filters"
import { DetailSlim, Photo, PhotoSlim, stripPhotoBinaryData, Tables } from "@/database/helpers/tables"
import { Repository } from "@/database/helpers/types"
import { InstahomePhotoType, PartialBy } from "@/utils/types"

import { db } from "../database"
import { PhotoWithDetailsSlim } from "../hooks"

type UpsertData = PartialBy<Parameters<Tables["photo"]["add"]>[0], "createdAt" | "updatedAt">
type UpdateData = StrictOmit<UpdateSpec<Photo>, "dexieId">

type FindAllParams = {
  sessionId: string
  includeDeleted?: boolean
}

type FindByWallParams = {
  sessionId: string
  wallNumber: number
  includeDeleted?: boolean
  includeDeletedDetails?: boolean
}

type FindByType = {
  sessionId: string
  type: InstahomePhotoType
  includeDeleted?: boolean
}

type FindByAmenityId = {
  sessionId: string
  amenityId: string
  includeDeleted?: boolean
}

type GetPhotoDetailsParams = {
  sessionId: string
  photoIds: string[]
  includeDeleted: boolean
}

async function getPhotoDetails(params: GetPhotoDetailsParams): Promise<Map<string, DetailSlim[]>> {
  const { sessionId, photoIds, includeDeleted } = params
  const slimDetails = await db.detail.findAll({ sessionId, includeDeleted })

  // Filter only details belonging to the requested photos
  const photoIdSet = new Set(photoIds)

  // Group details by their parent photo id
  const detailsMap = new Map<string, DetailSlim[]>()
  for (const detail of slimDetails) {
    if (!photoIdSet.has(detail.instahomePhotoId)) continue

    const existing = detailsMap.get(detail.instahomePhotoId) ?? []
    existing.push(detail)
    detailsMap.set(detail.instahomePhotoId, existing)
  }

  return detailsMap
}

export class PhotoRepository extends Repository {
  async add(data: UpsertData) {
    const currentDate = new Date()
    await this.dexie.photo.add({
      ...data,
      createdAt: data.createdAt ?? currentDate,
      updatedAt: data.updatedAt ?? currentDate,
    })
  }

  async upsert(photoData: UpsertData) {
    await this.dexie.transaction("rw", this.dexie.photo, async () => {
      const record = await this.dexie.photo.where("id").equals(photoData.id).first()
      if (record) {
        await this.dexie.photo.update(record.dexieId, { ...photoData })
        return
      }

      await this.add(photoData)
    })
  }

  async update(id: string, data: UpdateData) {
    await this.dexie.transaction("rw", this.dexie.photo, async () => {
      const photo = await this.findById(id)
      if (!photo) {
        return
      }

      await this.dexie.photo.update(photo.dexieId, { ...data, updatedAt: new Date() })
    })
  }

  async findAll(params: FindAllParams): Promise<PhotoSlim[]> {
    const { sessionId, includeDeleted = false } = params

    const photos = await this.dexie.photo
      .where({ instahomeSessionId: sessionId })
      .and(deletedFactory(includeDeleted))
      .toArray()

    return photos.map(stripPhotoBinaryData)
  }

  async findByWall(params: FindByWallParams): Promise<PhotoWithDetailsSlim[]> {
    const { sessionId, wallNumber, includeDeleted = false, includeDeletedDetails = false } = params

    const photos = await this.dexie.photo
      .where({ instahomeSessionId: sessionId })
      .and((photo) => photo.wallNumber === wallNumber)
      .and(deletedFactory(includeDeleted))
      .toArray()

    const detailsMap = await getPhotoDetails({
      sessionId,
      photoIds: photos.map((p) => p.id),
      includeDeleted: includeDeletedDetails,
    })

    // Combine photos with their details
    return photos.map((photo) => ({
      ...stripPhotoBinaryData(photo),
      details: detailsMap.get(photo.id) ?? [],
    }))
  }

  async findById(id: string): Promise<PhotoSlim | undefined> {
    const photo = await this.dexie.photo.where({ id }).first()
    return photo ? stripPhotoBinaryData(photo) : undefined
  }

  /** Returns full photo with binary data — for blob loading and upload */
  async findByIdFull(id: string): Promise<Photo | undefined> {
    return await this.dexie.photo.where({ id }).first()
  }

  async findByType(params: FindByType): Promise<PhotoSlim[]> {
    const { sessionId, type, includeDeleted = false } = params

    const photos = await this.dexie.photo
      .where({ instahomeSessionId: sessionId })
      .and((photo) => photo.type === type)
      .and(deletedFactory(includeDeleted))
      .toArray()

    return photos.map(stripPhotoBinaryData)
  }

  async findByAmenityId(params: FindByAmenityId): Promise<PhotoSlim[]> {
    const { sessionId, amenityId, includeDeleted = false } = params

    const photos = await this.dexie.photo
      .where({ instahomeSessionId: sessionId })
      .and((photo) => photo.amenityId === amenityId)
      .and(deletedFactory(includeDeleted))
      .toArray()

    return photos.map(stripPhotoBinaryData)
  }

  async markAsDeleted(photoId: string) {
    await this.dexie.transaction("rw", this.dexie.photo, async () => {
      const photo = await this.findById(photoId)
      if (!photo) {
        return
      }

      await this.dexie.photo.update(photo.dexieId, { deleted: true, updatedAt: new Date() })
    })
  }
}
